import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageBody } from "@/components/PageParts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RefreshCw, Search, ShieldAlert, Trash2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function AdminCleanup() {
  const { user } = useAuth();
  const [collections, setCollections] = useState([]);
  const [loadingCols, setLoadingCols] = useState(true);
  const [active, setActive] = useState(null);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState({ total: 0, rows: [] });
  const [loadingRows, setLoadingRows] = useState(false);
  const [target, setTarget] = useState(null);
  const [force, setForce] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [revision, setRevision] = useState(0);

  const loadCollections = async () => {
    setLoadingCols(true);
    try {
      const { data } = await api.get("/admin/collections");
      setCollections(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load collections"); }
    finally { setLoadingCols(false); }
  };
  useEffect(() => { loadCollections(); }, [revision]);

  useEffect(() => {
    if (!active) { setRows({ total: 0, rows: [] }); return; }
    const c = new AbortController(); setLoadingRows(true);
    api.get(`/admin/collections/${active}/rows`, {
      params: { search: query || undefined, limit: 100 }, signal: c.signal,
    }).then(({ data }) => setRows(data))
      .catch((e) => { if (!c.signal.aborted) toast.error(formatApiError(e.response?.data?.detail)); })
      .finally(() => { if (!c.signal.aborted) setLoadingRows(false); });
    return () => c.abort();
  }, [active, query, revision]);

  const activeMeta = useMemo(() => collections.find((c) => c.name === active), [collections, active]);
  const totalRows = collections.reduce((n, c) => n + Math.max(c.count, 0), 0);

  const doDelete = async () => {
    if (!target) return;
    setDeleting(true);
    try {
      const url = force && activeMeta?.cascade
        ? `/admin/collections/${active}/${target.id}?force=true`
        : `/admin/collections/${active}/${target.id}`;
      const { data } = await api.delete(url);
      if (data?.cascade) {
        const parts = Object.entries(data.cascade).filter(([, v]) => v > 0)
          .map(([k, v]) => `${v} ${k}`).join(", ");
        toast.success(parts ? `Deleted (cascade: ${parts})` : "Deleted");
      } else toast.success("Deleted");
      setTarget(null); setForce(false);
      setRevision((r) => r + 1);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
    finally { setDeleting(false); }
  };

  if (user?.role !== "superadmin") {
    return <PageBody><p className="text-sm text-slate-500">Super Admin only.</p></PageBody>;
  }

  return (
    <PageBody className="space-y-6" data-testid="admin-cleanup-page">
      <Card className="border-rose-200 bg-rose-50">
        <CardHeader className="flex-row items-start gap-3 space-y-0">
          <ShieldAlert className="w-5 h-5 text-rose-700 mt-0.5" />
          <div>
            <CardTitle className="text-base text-rose-900">Admin cleanup</CardTitle>
            <CardDescription className="text-rose-800">
              Every collection below can be pruned row-by-row. Deletes are audit-logged.
              For finance domains (sales, invoices, pro-formas, receipts, customers, transactions),
              enable <strong>Force cascade</strong> to remove all linked children in one action.
            </CardDescription>
          </div>
        </CardHeader>
      </Card>

      <div className="grid grid-cols-12 gap-6">
        {/* Left: collection picker */}
        <div className="col-span-4 xl:col-span-3">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-slate-700">Collections <span className="text-slate-400 font-normal">({totalRows} rows)</span></h2>
            <Button size="sm" variant="ghost" onClick={() => setRevision((r) => r + 1)} data-testid="admin-cleanup-refresh"><RefreshCw className="w-3.5 h-3.5" /></Button>
          </div>
          <div className="bg-white border rounded-md overflow-hidden max-h-[68vh] overflow-y-auto">
            {loadingCols && <p className="p-4 text-xs text-slate-500">Loading…</p>}
            {!loadingCols && collections.map((c) => (
              <button
                key={c.name}
                onClick={() => { setActive(c.name); setSearch(""); setQuery(""); }}
                className={`w-full flex items-center justify-between px-3 py-2 text-left text-xs border-b last:border-b-0 hover:bg-slate-50 ${active === c.name ? "bg-slate-100 font-semibold" : ""}`}
                data-testid={`admin-cleanup-col-${c.name}`}
              >
                <span className="font-mono">{c.name}</span>
                <span className="flex items-center gap-2">
                  {c.cascade && <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700 text-[10px] font-normal">cascade</Badge>}
                  <span className="text-slate-500 font-mono tabular-nums">{c.count}</span>
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Right: rows in selected collection */}
        <div className="col-span-8 xl:col-span-9">
          {!active ? (
            <div className="border rounded-md p-8 text-sm text-slate-500 bg-white">
              Pick a collection on the left to see its rows.
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <h2 className="text-base font-semibold" data-testid="admin-cleanup-active-name">{active}</h2>
                <Badge variant="outline">{rows.total} rows</Badge>
                {activeMeta?.cascade && <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">supports cascade</Badge>}
                <form className="flex gap-2 ml-auto" onSubmit={(e) => { e.preventDefault(); setQuery(search); }}>
                  <Input placeholder="Search id / number / name / email…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-72" data-testid="admin-cleanup-search" />
                  <Button type="submit" variant="outline" size="icon" aria-label="Search" data-testid="admin-cleanup-search-btn"><Search className="w-4 h-4" /></Button>
                </form>
              </div>

              <div className="bg-white border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-24">Type</TableHead>
                      <TableHead>Label</TableHead>
                      <TableHead className="w-72">ID</TableHead>
                      <TableHead className="w-20 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingRows && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-6">Loading…</TableCell></TableRow>}
                    {!loadingRows && rows.rows.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-6">No rows.</TableCell></TableRow>}
                    {rows.rows.map((r) => (
                      <TableRow key={r.id || Math.random()} data-testid={`admin-cleanup-row-${r.id}`}>
                        <TableCell className="text-xs text-slate-500 font-mono">{active}</TableCell>
                        <TableCell className="text-sm">{r._label}</TableCell>
                        <TableCell className="text-xs font-mono text-slate-500">{r.id || <span className="italic">no id field</span>}</TableCell>
                        <TableCell className="text-right">
                          {r.id && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                              onClick={() => { setTarget(r); setForce(false); }}
                              aria-label={`Delete ${r._label}`}
                              data-testid={`admin-cleanup-delete-${r.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </div>
      </div>

      <AlertDialog open={!!target} onOpenChange={(v) => { if (!v && !deleting) { setTarget(null); setForce(false); } }}>
        <AlertDialogContent data-testid="admin-cleanup-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {target?._label}?</AlertDialogTitle>
            <AlertDialogDescription>
              Collection: <span className="font-mono">{active}</span><br />
              ID: <span className="font-mono">{target?.id}</span><br />
              This is audit-logged. {activeMeta?.cascade && "You can also force-cascade to remove every linked child in one call."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {activeMeta?.cascade && (
            <label className="flex items-start gap-2 rounded border border-rose-200 bg-rose-50 p-3 text-sm">
              <Checkbox checked={force} onCheckedChange={setForce} data-testid="admin-cleanup-force" />
              <span>
                <span className="font-semibold text-rose-800">Force cascade</span>
                <span className="block text-xs text-rose-700 mt-0.5">
                  Removes every linked child record (allocations, receipts, invoices, pro-formas,
                  gateway transactions, on-hold deposits — whichever apply).
                </span>
              </span>
            </label>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="admin-cleanup-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={(e) => { e.preventDefault(); doDelete(); }}
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
              data-testid="admin-cleanup-confirm-action"
            >
              {deleting ? "Deleting…" : force ? "Force delete" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageBody>
  );
}
